import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import useNavigator from "@saleor/hooks/useNavigator";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { SearchBrandsPrivate_search_edges_node } from "@saleor/searches/types/SearchBrandsPrivate";
import { SearchClusters_search_edges_node } from "@saleor/searches/types/SearchClusters";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { FetchMoreProps } from "@saleor/types";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { maybe } from "../../../misc";
import {
  SmopRentalTypeEnum,
  SmopStatusEnum,
  SubTenantTypeEnum
} from "../../../types/globalTypes";
import { storeListPath } from "../../urls";
import StoreForm from "../StoreForm/StoreForm";
import { hasNoError, useValidation } from "./StoreCreateValidation";

export interface StoreCreatePageFormData {
  authorizeEmailAddress: string;
  authorizeMobileNum: string;
  acctName: string;
  acctNum: string;
  bankDetails: any;
  bankName: string;
  cluster: string;
  contractId: string;
  designation: string;
  financeName: string;
  financeEmailAddress: string;
  financeMobileNum: string;
  mallBranch: string;
  sellerBrand: string;
  signatoryName: string;
  ipay88SubMid: string;
  isActivate: any;
  isActivateCompanyDetails: boolean;
  isExpressLongDistanceDeliveryAllowed: boolean;
  isFeatured: boolean;
  isSameDayDeliveryAllowed: boolean;
  location: string;
  operationEndTime: string;
  operationStartTime: string;
  promoRibbonDescription: string;
  promoRibbonEnd: string;
  promoRibbonStart: string;
  storeType: any;
  storeName: string;
  paymentFeeRate: string;
  rentalType: SmopRentalTypeEnum;
  tinNo: string;
  isUseTenantCluster: boolean;
  bannerImage: {
    alt: string;
    url: string;
  };
  subTenantType: SubTenantTypeEnum | null;
  isInMallPickup: boolean;
  isInStorePickup: boolean;
  isMallStandardDeliveryEnabled: boolean;
  isStandardDeliveryEnabled: boolean;
}

export interface StoreCreatePageProps {
  /** Form actions */
  onSubmit: (data: StoreCreatePageFormData, image: File) => void;
  discardButton: any;

  /** Form state */
  disable: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;

  /** Data */
  brands: SearchBrandsPrivate_search_edges_node[];
  malls: SearchMalls_search_edges_node[];
  clusters: SearchClusters_search_edges_node[];
  paymentFeeRateChoices: any;

  /** Fetch actions */
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchClusters: (query: string) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreClusters?: FetchMoreProps;
}

const StoreCreatePage: React.FC<StoreCreatePageProps> = props => {
  const {
    /** Form actions */
    onSubmit,
    discardButton,

    /** Form state */
    disable,
    saveButtonBarState,

    /** Data */
    brands,
    malls,
    clusters,
    paymentFeeRateChoices,

    /** Fetch actions */
    fetchMalls,
    fetchBrands,
    fetchClusters,
    fetchMoreMalls,
    fetchMoreBrands,
    fetchMoreClusters
  } = props;
  const intl = useIntl();
  const navigate = useNavigator();

  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  const [imageToUpload, setImageToUpload] = useState(null);
  const [mallDisplayName, setMallDisplayName] = useStateFromProps("");
  const [tenantStoreType, setTenantStoreType] = useStateFromProps("");
  const [clusterDisplayName, setClusterDisplayName] = useStateFromProps("");
  const [paymentRateDisplay, setPaymentRateDisplay] = useStateFromProps("");
  const [rentTypeDisplay, setRentTypeDisplay] = useStateFromProps("");
  const [subTenantTypeDisplay, setSubTenantTypeDisplay] = useStateFromProps("");

  const initialForm: StoreCreatePageFormData = {
    acctName: "",
    acctNum: "",
    authorizeEmailAddress: "",
    authorizeMobileNum: "",
    bankDetails: "",
    bankName: "",
    bannerImage: {
      alt: "",
      url: ""
    },
    cluster: "",
    contractId: "",
    designation: "",
    financeEmailAddress: "",
    financeMobileNum: "",
    financeName: "",
    ipay88SubMid: "",
    isActivate: SmopStatusEnum.INA,
    isActivateCompanyDetails: false,
    isExpressLongDistanceDeliveryAllowed: false,
    isFeatured: false,
    isInMallPickup: false,
    isInStorePickup: true,
    isSameDayDeliveryAllowed: false,
    isMallStandardDeliveryEnabled: false,
    isStandardDeliveryEnabled: false,
    isUseTenantCluster: false,
    location: "",
    mallBranch: "",
    operationEndTime: null,
    operationStartTime: null,
    paymentFeeRate: null,
    promoRibbonDescription: "",
    promoRibbonEnd: "",
    promoRibbonStart: "",
    rentalType: null,
    sellerBrand: "",
    signatoryName: "",
    storeName: "",
    storeType: tenantStoreType,
    subTenantType: null,
    tinNo: ""
  };

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );
  const storeType = maybe(
    () =>
      brands.map(brand => ({
        label: brand.storeType,
        value: brand?.id
      })),
    []
  );

  const mallChoices = maybe(
    () =>
      malls.map(mall => ({
        label: mall?.name,
        value: mall?.id
      })),
    []
  );
  const paymentRateChoices = maybe(
    () =>
      paymentFeeRateChoices?.paymentFee.map(rate => ({
        label: rate.rate.toString(),
        value: rate.rate.toFixed(2)
      })),
    []
  );

  const clusterChoices = maybe(() =>
    clusters.map(cluster => ({
      label: cluster.name,
      value: cluster.id
    }))
  );

  const rentTypeChoices = [
    { label: "Fixed", value: SmopRentalTypeEnum.FIXED },
    { label: "Percentage", value: SmopRentalTypeEnum.PERCENTAGE }
  ];

  const subTenantChoices = [
    { label: "Food Court", value: SubTenantTypeEnum.FOOD_COURT },
    { label: "Non-Food Court", value: SubTenantTypeEnum.NON_FOOD_COURT }
  ];

  const initialErrorValidation = {
    acctName: null,
    acctNum: null,
    authorizeEmailAddress: null,
    authorizeMobileNum: null,
    bankName: null,
    cluster: null,
    contractId: null,
    designation: null,
    financeEmailAddress: null,
    financeMobileNum: null,
    financeName: null,
    ipay88SubMid: null,
    location: null,
    mallBranch: null,
    paymentFeeRate: null,
    promoRibbonDescription: null,
    promoRibbonEnd: null,
    promoRibbonStart: null,
    rentalType: null,
    sellerBrand: null,
    signatoryName: null,
    subTenantType: null
  };

  const [errorValidation, setErrorValidation] = useState(
    initialErrorValidation
  );

  const handleSubmit = event => {
    const validate = useValidation(event);
    if (hasNoError(validate)) {
      setErrorValidation(validate);
      onSubmit(event, imageToUpload);
    } else {
      setErrorValidation(validate);
    }
  };

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit, hasChanged }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );
        const handleSelectBrandStoreType = createSingleAutocompleteSelectHandler(
          change,
          setTenantStoreType,
          storeType
        );

        const handleSelectMall = createSingleAutocompleteSelectHandler(
          change,
          setMallDisplayName,
          mallChoices
        );

        const handlePaymentRate = createSingleAutocompleteSelectHandler(
          change,
          setPaymentRateDisplay,
          paymentRateChoices
        );

        const handleRentType = createSingleAutocompleteSelectHandler(
          change,
          setRentTypeDisplay,
          rentTypeChoices
        );

        const handleSelectCluster = createSingleAutocompleteSelectHandler(
          change,
          setClusterDisplayName,
          clusterChoices
        );

        const handleSubTenantType = createSingleAutocompleteSelectHandler(
          change,
          setSubTenantTypeDisplay,
          subTenantChoices
        );

        const handleDiscardModal = () => {
          if (hasChanged) {
            discardButton(true);
          } else {
            navigate(storeListPath);
          }
        };

        const breadcrumbDirectory = [
          {
            name: "Stores",
            url: "/store"
          },
          {
            name: "Create Store"
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={handleDiscardModal} />
              <PageHeader
                title={intl.formatMessage({
                  defaultMessage: "Create Store",
                  description: "page header"
                })}
              />
            </div>
            <StoreForm
              data={formData}
              change={change}
              submit={submit}
              error={errorValidation}
              clusterDisplayValue={clusterDisplayName}
              sellerDisplayValue={sellerDisplayName}
              saveButtonBarState={saveButtonBarState}
              mallDisplayValue={mallDisplayName}
              brands={sellerBrandChoices}
              malls={mallChoices}
              clusters={clusterChoices}
              fetchMalls={query => fetchMalls(query)}
              fetchBrands={query => fetchBrands(query)}
              fetchClusters={query => fetchClusters(query)}
              fetchMoreMalls={fetchMoreMalls}
              fetchMoreBrands={fetchMoreBrands}
              fetchMoreClusters={fetchMoreClusters}
              onImageUpload={setImageToUpload}
              onBrandChangeStoreType={handleSelectBrandStoreType}
              paymentRateChoices={paymentRateChoices}
              rentTypeChoices={rentTypeChoices}
              paymentRateDisplay={paymentRateDisplay}
              rentTypeDisplay={rentTypeDisplay}
              onClusterChange={handleSelectCluster}
              onBrandChange={handleSelectBrand}
              onMallChange={handleSelectMall}
              onPaymentChange={handlePaymentRate}
              onRentalChange={handleRentType}
              hasChange={hasChanged}
              discardButton={handleDiscardModal}
              disable={disable}
              subTenantChoices={subTenantChoices}
              onSubTenantTypeChange={handleSubTenantType}
              subTenantTypeDisplay={subTenantTypeDisplay}
            />
          </Container>
        );
      }}
    </Form>
  );
};
StoreCreatePage.displayName = "StoreCreatePage";
export default StoreCreatePage;
